import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import {
  User,
  Bell,
  Shield,
  Palette,
  DollarSign,
  Download,
  Upload,
  Trash2,
  Save,
  Eye,
  EyeOff,
  Key,
  Mail,
  Phone,
  Building,
  Calculator,
  FileText,
  Database,
  AlertTriangle,
  Settings,
  Hammer,
  ArrowRight,
  CheckCircle,
  RefreshCw,
  Edit3,
  Check,
  X
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { templatesApi, reportsApi } from '@/services/api'

export const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  // Mock user data - would come from API
  const [profileData, setProfileData] = useState({
    firstName: 'Demo',
    lastName: 'User',
    email: 'demo@example.com',
    phone: '',
    company: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  })

  const [businessSettings, setBusinessSettings] = useState({
    businessName: 'My Jewelry Business',
    defaultLaborRate: '25.00',
    defaultMarkup: '200',
    taxRate: '8.25',
    currency: 'USD',
    timezone: 'America/New_York',
    fiscalYearStart: 'January'
  })

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    lowStockAlerts: true,
    projectDeadlines: true,
    weeklyReports: false,
    marketingEmails: false
  })

  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    sessionTimeout: '30',
    passwordLastChanged: '2024-01-15'
  })

  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'system',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12',
    compactMode: false
  })

  // Template management state
  const [currentTemplate, setCurrentTemplate] = useState<any>(null)
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([])
  const [templateLoading, setTemplateLoading] = useState(false)
  const [userTemplates, setUserTemplates] = useState<any[]>([])
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null)
  const [editingName, setEditingName] = useState<string>('')

  // Export state
  const [exportLoading, setExportLoading] = useState(false)

  const navigate = useNavigate()

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'business', label: 'Business', icon: Building },
    { id: 'templates', label: 'Templates', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'data', label: 'Data & Export', icon: Download }
  ]

  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplateData()
    }
  }, [activeTab])

  const fetchTemplateData = async () => {
    try {
      setTemplateLoading(true)
      
      // Load user templates first (most important for the page)
      const userTemplatesData = await templatesApi.getUserTemplates()
      setUserTemplates(userTemplatesData)
      setCurrentTemplate(userTemplatesData.find((t: any) => t.isDefault) || null)
      
      // Load basic templates data separately with timeout and fallback
      try {
        const allTemplatesPromise = templatesApi.getAllBasic()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Template loading timeout')), 10000)
        )
        
        const allTemplates = await Promise.race([allTemplatesPromise, timeoutPromise])
        setAvailableTemplates(allTemplates as any[])
      } catch (templateError) {
        console.warn('Failed to load all templates, using fallback:', templateError)
        // Fallback: use empty array for statistics
        setAvailableTemplates([])
      }
    } catch (error) {
      console.error('Error fetching template data:', error)
      // Set empty states on error
      setUserTemplates([])
      setCurrentTemplate(null)
      setAvailableTemplates([])
    } finally {
      setTemplateLoading(false)
    }
  }

  const handleSwitchTemplate = () => {
    navigate('/templates/switch')
  }

  const handleEditCustomName = (templateId: string, currentName: string) => {
    setEditingTemplate(templateId)
    setEditingName(currentName || '')
  }

  const handleSaveCustomName = async (templateId: string) => {
    try {
      setLoading(true)
      await templatesApi.updateCustomBusinessName(templateId, editingName || null)
      
      // Update local state
      setUserTemplates(prev => prev.map(t =>
        t.templateId === templateId
          ? { ...t, customBusinessName: editingName || null }
          : t
      ))
      
      // Update current template if it's the one being edited
      if (currentTemplate?.templateId === templateId) {
        setCurrentTemplate((prev: any) => ({ ...prev, customBusinessName: editingName || null }))
      }
      
      // Dispatch template change event to update sidebar and other components
      window.dispatchEvent(new CustomEvent('templateChanged'))
      
      setEditingTemplate(null)
      setEditingName('')
    } catch (error) {
      console.error('Error updating custom business name:', error)
      alert('Failed to update custom business name. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingTemplate(null)
    setEditingName('')
  }

  const handleSave = async (section: string) => {
    setLoading(true)
    try {
      // TODO: Save to API
      console.log(`Saving ${section} settings`)
      await new Promise(resolve => setTimeout(resolve, 1000))
    } catch (error) {
      console.error('Error saving settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleQuickBooksExport = async (format: 'json' | 'csv' = 'csv') => {
    setExportLoading(true)
    try {
      console.log(`🚀 Starting QuickBooks export in ${format.toUpperCase()} format`)
      
      await reportsApi.exportQuickBooks({
        format,
        includeProjects: true,
        includePurchases: true,
        includeTimeTracking: true,
      })
      
      console.log('✅ QuickBooks export completed successfully')
    } catch (error) {
      console.error('❌ QuickBooks export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setExportLoading(false)
    }
  }

  const handleDataExport = async (format: 'json' | 'csv' | 'excel') => {
    setExportLoading(true)
    try {
      console.log(`🚀 Starting data export in ${format.toUpperCase()} format`)
      
      // For general data export, use analytics export
      await reportsApi.exportAnalytics({
        format: format as 'json' | 'csv' | 'excel',
      })
      
      console.log('✅ Data export completed successfully')
    } catch (error) {
      console.error('❌ Data export failed:', error)
      alert('Export failed. Please try again.')
    } finally {
      setExportLoading(false)
    }
  }

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Personal Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              First Name
            </label>
            <input
              type="text"
              value={profileData.firstName}
              onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={profileData.lastName}
              onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <Button
            onClick={() => handleSave('profile')}
            loading={loading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Profile
          </Button>
        </div>
      </div>
    </div>
  )

  const renderBusinessTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Business Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Business Name
            </label>
            <input
              type="text"
              value={businessSettings.businessName}
              onChange={(e) => setBusinessSettings(prev => ({ ...prev, businessName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Default Labor Rate ($/hour)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="number"
                step="0.01"
                value={businessSettings.defaultLaborRate}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, defaultLaborRate: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Default Markup (%)
            </label>
            <div className="relative">
              <Calculator className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="number"
                value={businessSettings.defaultMarkup}
                onChange={(e) => setBusinessSettings(prev => ({ ...prev, defaultMarkup: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tax Rate (%)
            </label>
            <input
              type="number"
              step="0.01"
              value={businessSettings.taxRate}
              onChange={(e) => setBusinessSettings(prev => ({ ...prev, taxRate: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            />
          </div>
        </div>
        
        <div className="mt-6">
          <Button
            onClick={() => handleSave('business')}
            loading={loading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Business Settings
          </Button>
        </div>
      </div>
    </div>
  )

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Notification Preferences
        </h3>
        
        <div className="space-y-4">
          {Object.entries(notificationSettings).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {key === 'emailNotifications' && 'Receive email notifications for important updates'}
                  {key === 'lowStockAlerts' && 'Get notified when materials are running low'}
                  {key === 'projectDeadlines' && 'Reminders for upcoming project deadlines'}
                  {key === 'weeklyReports' && 'Weekly summary reports via email'}
                  {key === 'marketingEmails' && 'Product updates and marketing communications'}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jewelry-primary/20 dark:peer-focus:ring-jewelry-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-jewelry-primary"></div>
              </label>
            </div>
          ))}
        </div>
        
        <div className="mt-6">
          <Button
            onClick={() => handleSave('notifications')}
            loading={loading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Notification Settings
          </Button>
        </div>
      </div>
    </div>
  )

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Security Settings
        </h3>
        
        <div className="space-y-6">
          {/* Password Change */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-4">Change Password</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                />
              </div>
            </div>
            <Button className="mt-4" variant="outline">
              Update Password
            </Button>
          </div>
          
          {/* Two-Factor Authentication */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Two-Factor Authentication</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Add an extra layer of security to your account
                </p>
              </div>
              <Badge variant={securitySettings.twoFactorEnabled ? 'success' : 'secondary'}>
                {securitySettings.twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <Button className="mt-4" variant="outline">
              {securitySettings.twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
            </Button>
          </div>
        </div>
        
        <div className="mt-6">
          <Button
            onClick={() => handleSave('security')}
            loading={loading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Security Settings
          </Button>
        </div>
      </div>
    </div>
  )

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Appearance & Localization
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Theme
            </label>
            <select
              value={appearanceSettings.theme}
              onChange={(e) => setAppearanceSettings(prev => ({ ...prev, theme: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="system">System</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Language
            </label>
            <select
              value={appearanceSettings.language}
              onChange={(e) => setAppearanceSettings(prev => ({ ...prev, language: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date Format
            </label>
            <select
              value={appearanceSettings.dateFormat}
              onChange={(e) => setAppearanceSettings(prev => ({ ...prev, dateFormat: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            >
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Time Format
            </label>
            <select
              value={appearanceSettings.timeFormat}
              onChange={(e) => setAppearanceSettings(prev => ({ ...prev, timeFormat: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
            >
              <option value="12">12-hour (AM/PM)</option>
              <option value="24">24-hour</option>
            </select>
          </div>
        </div>
        
        <div className="mt-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div>
              <div className="font-medium text-gray-900 dark:text-white">Compact Mode</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Use smaller spacing and condensed layouts
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={appearanceSettings.compactMode}
                onChange={(e) => setAppearanceSettings(prev => ({ ...prev, compactMode: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-jewelry-primary/20 dark:peer-focus:ring-jewelry-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-jewelry-primary"></div>
            </label>
          </div>
        </div>
        
        <div className="mt-6">
          <Button
            onClick={() => handleSave('appearance')}
            loading={loading}
            leftIcon={<Save className="w-4 h-4" />}
          >
            Save Appearance Settings
          </Button>
        </div>
      </div>
    </div>
  )

  const renderDataTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Data Management
        </h3>
        
        <div className="space-y-6">
          {/* Export Data */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-4">Export Data</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Download your data in various formats for backup or migration purposes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                leftIcon={<Download className="w-4 h-4" />}
                onClick={() => handleDataExport('json')}
                loading={exportLoading}
                disabled={exportLoading}
              >
                Export as JSON
              </Button>
              <Button
                variant="outline"
                leftIcon={<Download className="w-4 h-4" />}
                onClick={() => handleDataExport('csv')}
                loading={exportLoading}
                disabled={exportLoading}
              >
                Export as CSV
              </Button>
              <Button
                variant="outline"
                leftIcon={<Download className="w-4 h-4" />}
                onClick={() => handleDataExport('excel')}
                loading={exportLoading}
                disabled={exportLoading}
              >
                Export as Excel
              </Button>
            </div>
            
            {/* QuickBooks Export */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1">
                  <h5 className="font-medium text-blue-800 dark:text-blue-200">QuickBooks Integration</h5>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Export your inventory data, costs, and transactions in QuickBooks-compatible format for seamless accounting integration.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/20"
                      leftIcon={<Download className="w-4 h-4" />}
                      onClick={() => handleQuickBooksExport('csv')}
                      loading={exportLoading}
                      disabled={exportLoading}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/20"
                      leftIcon={<Download className="w-4 h-4" />}
                      onClick={() => handleQuickBooksExport('json')}
                      loading={exportLoading}
                      disabled={exportLoading}
                    >
                      Export JSON
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Import Data */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
            <h4 className="font-medium text-gray-900 dark:text-white mb-4">Import Data</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Import data from other systems or restore from backup.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button variant="outline" leftIcon={<Upload className="w-4 h-4" />}>
                Import Materials
              </Button>
              <Button variant="outline" leftIcon={<Upload className="w-4 h-4" />}>
                Import Projects
              </Button>
            </div>
          </div>
          
          {/* Delete Account */}
          <div>
            <h4 className="font-medium text-red-600 dark:text-red-400 mb-4">Danger Zone</h4>
            <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                <div className="flex-1">
                  <h5 className="font-medium text-red-800 dark:text-red-200">Delete Account</h5>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-3 text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                  >
                    Delete Account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  const renderTemplatesTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          Business Template Management
        </h3>
        
        {templateLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Template */}
            {currentTemplate && (
              <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">Current Template</h4>
                <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                        <Hammer className="w-6 h-6" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 dark:text-white">
                          {currentTemplate?.template?.name || currentTemplate?.name || 'Unknown Template'}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {currentTemplate?.template?.industry || currentTemplate?.industry || 'Unknown'} • {currentTemplate?.template?.businessType || currentTemplate?.businessType || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                          {currentTemplate?.template?.description || currentTemplate?.description || 'No description available'}
                        </p>
                      </div>
                    </div>
                    <Badge variant="success" size="sm">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Active
                    </Badge>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {currentTemplate?.template?.categoriesCount || currentTemplate?.categoriesCount || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">Categories</div>
                    </div>
                    <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {currentTemplate?.template?.projectTypesCount || currentTemplate?.projectTypesCount || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">Project Types</div>
                    </div>
                    <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {currentTemplate?.template?.activityTypesCount || currentTemplate?.activityTypesCount || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">Activities</div>
                    </div>
                    <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {currentTemplate?.template?.unitTypesCount || currentTemplate?.unitTypesCount || 0}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400">Unit Types</div>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Template Actions */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">Template Actions</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <div className="flex items-start space-x-3">
                    <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 dark:text-white">Switch Template</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Change to a different business template. Your existing data will be preserved and adapted.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={handleSwitchTemplate}
                        rightIcon={<ArrowRight className="w-4 h-4" />}
                      >
                        Browse Templates
                      </Button>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-start space-x-3">
                    <Settings className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="font-medium text-gray-900 dark:text-white">Customize Template</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Modify categories, project types, and other template settings to fit your specific needs.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        disabled
                        rightIcon={<ArrowRight className="w-4 h-4" />}
                      >
                        Coming Soon
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            {/* Template Statistics */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">Template Statistics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {availableTemplates.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Available Templates
                  </div>
                </Card>
                
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {new Set(availableTemplates.map(t => t.industry)).size}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Industries Covered
                  </div>
                </Card>
                
                <Card className="p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {currentTemplate ? new Date(currentTemplate.updatedAt).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Last Updated
                  </div>
                </Card>
              </div>
            </div>

            {/* Custom Business Names Management */}
            {userTemplates.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-4">Custom Business Names</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Personalize your templates by setting custom business names. This helps you identify different business ventures or brands.
                </p>
                <div className="space-y-3">
                  {userTemplates.map((userTemplate) => (
                    <Card key={userTemplate.id} className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                            <Hammer className="w-5 h-5" />
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-white">
                              {userTemplate.template?.name || 'Unknown Template'}
                            </h5>
                            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                              {userTemplate.template?.industry} • {userTemplate.template?.businessType}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {userTemplate.isDefault && (
                            <Badge variant="success" size="sm">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Custom Business Name
                        </label>
                        {editingTemplate === userTemplate.templateId ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              placeholder="Enter custom business name (optional)"
                              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                              maxLength={255}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleSaveCustomName(userTemplate.templateId)}
                              loading={loading}
                              leftIcon={<Check className="w-4 h-4" />}
                              style={{
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                border: '1px solid #3B82F6'
                              }}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                              leftIcon={<X className="w-4 h-4" />}
                              style={{
                                backgroundColor: 'white',
                                color: '#374151',
                                border: '1px solid #D1D5DB'
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              {userTemplate.customBusinessName ? (
                                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                  <span className="text-gray-900 dark:text-white font-medium">
                                    {userTemplate.customBusinessName}
                                  </span>
                                </div>
                              ) : (
                                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                  <span className="text-gray-500 dark:text-gray-400 italic">
                                    No custom name set
                                  </span>
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditCustomName(userTemplate.templateId, userTemplate.customBusinessName || '')}
                              leftIcon={<Edit3 className="w-4 h-4" />}
                              className="ml-3"
                            >
                              Edit
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h5 className="font-medium text-gray-900 dark:text-white mb-2">About Templates</h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Templates provide industry-specific categories, project types, activity types, and financial configurations
                tailored to different hobby businesses. Switching templates will adapt your system to better match your
                business type while preserving your existing data.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile': return renderProfileTab()
      case 'business': return renderBusinessTab()
      case 'templates': return renderTemplatesTab()
      case 'notifications': return renderNotificationsTab()
      case 'security': return renderSecurityTab()
      case 'appearance': return renderAppearanceTab()
      case 'data': return renderDataTab()
      default: return renderProfileTab()
    }
  }

  return (
    <>
      <Helmet>
        <title>Settings - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Settings
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your account, business preferences, and application settings
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-jewelry-primary text-jewelry-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </>
  )
}